import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

type Item = { id: string; label: string; hint?: string; section: string; action: () => void };

const STATIC_ITEMS = (nav: ReturnType<typeof useNavigate>): Item[] => [
  { id: "onboarding", section: "Navigation", label: "Geführte Einführung", hint: "Startpunkt des Produktflows", action: () => nav("/app/onboarding") },
  { id: "control-system", section: "Navigation", label: "Steuerungssystem", hint: "Entscheidungen, Maßnahmen und Wirkung", action: () => nav("/app/steuerungssystem") },
  { id: "company", section: "Navigation", label: "Unternehmen", hint: "Mandantenprofil und Beraterkontext", action: () => nav("/app/company") },
  { id: "potential", section: "Navigation", label: "KI-Potenzialanalyse", hint: "Reifegrad und Quick Wins", action: () => nav("/app/potential") },
  { id: "process-landscape", section: "Navigation", label: "Prozesslandschaft", hint: "12 Phasen · 59 Teilprozesse · 26 Agenten", action: () => nav("/app/process-landscape") },
  { id: "transformation", section: "Navigation", label: "Transformation OS", hint: "1&1 IMPULSE · CMB · KEP · Blue", action: () => nav("/app/transformation") },
  { id: "cc", section: "Navigation", label: "Command Center", hint: "Entscheidungslage", action: () => nav("/app/command-center") },
  { id: "advisor", section: "Navigation", label: "KI-Berater", hint: "4 Modi · 12 Personas", action: () => nav("/app/advisor") },
  { id: "sources", section: "Navigation", label: "Datenquellen", hint: "CSV, REST, Crawl, Nimble", action: () => nav("/app/sources") },
  { id: "benchmarks", section: "Navigation", label: "Benchmarks", hint: "Marktvergleich und Ranking", action: () => nav("/app/benchmarks") },
  { id: "use-cases", section: "Navigation", label: "KI Use Cases", hint: "Impact, Readiness, Risiko", action: () => nav("/app/use-cases") },
  { id: "roadmap", section: "Navigation", label: "Roadmap", hint: "90-Tage-Agentenplan", action: () => nav("/app/roadmap") },
  { id: "achievements", section: "Navigation", label: "Achievements", hint: "Evidence-basierter Fortschritt", action: () => nav("/app/achievements") },
  { id: "metrics", section: "Navigation", label: "KPI-Definitionen", action: () => nav("/app/metrics") },
  { id: "trees", section: "Navigation", label: "Driver Trees", action: () => nav("/app/trees") },
  { id: "roi", section: "Navigation", label: "ROI / Monte-Carlo", hint: "Szenario-Business-Case", action: () => nav("/app/roi") },
  { id: "briefings", section: "Navigation", label: "Briefings", action: () => nav("/app/briefings") },
  { id: "agents", section: "Navigation", label: "Agent Runs", action: () => nav("/app/agents") },
  { id: "candidates", section: "Navigation", label: "KPI Kandidaten", action: () => nav("/app/candidates") },
  { id: "compliance", section: "Navigation", label: "Compliance", hint: "EU AI Act Art. 12", action: () => nav("/app/compliance") },
  { id: "ask-cfo-roi", section: "Aktion", label: "KI-Berater: ROI-Rechnung als CFO starten", action: () => nav("/app/advisor?mode=roi&persona=CFO") },
  { id: "ask-process", section: "Aktion", label: "KI-Berater: Prozessschritt planen", action: () => nav("/app/advisor?mode=strategy&persona=Berater&msg=Plane%20den%20naechsten%20KPI-Mining-Prozessschritt%20mit%20Agenten%2C%20Quality-Gates%2C%20Evidence%20und%20HITL.") },
  { id: "ask-ceo-strategy", section: "Aktion", label: "KI-Berater: Strategie-Sparring als CEO", action: () => nav("/app/advisor") },
  { id: "upload-csv", section: "Aktion", label: "CSV-Datei hochladen", action: () => nav("/app/sources") },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const nav = useNavigate();
  const items = useMemo(() => STATIC_ITEMS(nav), [nav]);
  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () => (q ? items.filter((i) => i.label.toLowerCase().includes(q) || (i.hint || "").toLowerCase().includes(q)) : items),
    [items, q]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        setQuery("");
        setActive(0);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  useEffect(() => { setActive(0); }, [q]);

  if (!open) return null;

  return (
    <div className="cmdk-overlay" onClick={() => setOpen(false)}>
      <div className="cmdk-panel" onClick={(e) => e.stopPropagation()}>
        <div className="cmdk-input-row">
          <input
            ref={inputRef}
            className="cmdk-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suche oder Befehl …"
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(filtered.length - 1, a + 1)); }
              if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
              if (e.key === "Enter") {
                const item = filtered[active];
                if (item) { item.action(); setOpen(false); }
              }
            }}
          />
          <kbd>Esc</kbd>
        </div>
        <ul className="cmdk-list">
          {filtered.length === 0 && <li className="cmdk-empty">Keine Treffer.</li>}
          {filtered.map((it, i) => (
            <li
              key={it.id}
              className={`cmdk-item ${i === active ? "active" : ""}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => { it.action(); setOpen(false); }}
            >
              <span className="cmdk-section">{it.section}</span>
              <span className="cmdk-label">{it.label}</span>
              {it.hint && <span className="cmdk-hint">{it.hint}</span>}
            </li>
          ))}
        </ul>
        <div className="cmdk-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigieren</span>
          <span><kbd>↵</kbd> öffnen</span>
          <span><kbd>⌘</kbd><kbd>K</kbd> umschalten</span>
        </div>
      </div>
    </div>
  );
}
